// Sopwith 3D - object pool and lists.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Objects live on two doubly-linked lists: the main list in creation order
// (which is the order they are moved each tick) and an X-sorted list used by
// collision detection. Freed objects are parked on a delete list until the
// next tick so nothing referencing them in the current tick dangles, then
// recycled through a free list. Recycling order affects which Obj instance a
// new object reuses, so it is kept identical to the original.

import { movexy } from './fixed';
import { Obj, ObjState } from './types';

export class ObjectWorld {
  /** Main list. */
  objtop: Obj | null = null;
  objbot: Obj | null = null;
  /** Pending deletes and the free list. */
  deltop: Obj | null = null;
  delbot: Obj | null = null;
  objfree: Obj | null = null;
  /** Sentinels of the X-sorted list. */
  readonly topobj = new Obj();
  readonly botobj = new Obj();
  private nextId = 1;

  constructor() {
    this.reset();
  }

  reset(): void {
    this.topobj.xnext = this.topobj.next = this.botobj;
    this.botobj.xprev = this.botobj.prev = this.topobj;
    this.topobj.x = -32767;
    this.botobj.x = 32767;
    this.objbot = this.objtop = this.deltop = this.delbot = null;
    this.objfree = null;
  }

  /** Move the delete list onto the free list (called at the start of a tick). */
  recycleDeleted(): void {
    if (this.deltop) {
      this.delbot!.next = this.objfree;
      this.objfree = this.deltop;
      this.deltop = this.delbot = null;
    }
  }

  allocobj(): Obj {
    let ob: Obj;
    if (this.objfree) {
      ob = this.objfree;
      this.objfree = ob.next;
      resetObj(ob);
    } else {
      ob = new Obj();
    }
    ob.id = this.nextId++;
    ob.next = null;
    ob.prev = this.objbot;
    if (this.objbot) {
      this.objbot.next = ob;
    } else {
      this.objtop = ob;
    }
    ob.drwflg = false;
    ob.onmap = false;
    this.objbot = ob;
    return ob;
  }

  deallobj(ob: Obj): void {
    this.deletex(ob);
    const prev = ob.prev;
    if (prev) {
      prev.next = ob.next;
    } else {
      this.objtop = ob.next;
    }
    const next = ob.next;
    if (next) {
      next.prev = ob.prev;
    } else {
      this.objbot = ob.prev;
    }
    ob.next = null;
    if (this.delbot) {
      this.delbot.next = ob;
    } else {
      this.deltop = ob;
    }
    this.delbot = ob;
  }

  /** Insert `ob` into the X-sorted list, searching from `obp`. */
  insertx(ob: Obj, obp: Obj): void {
    let obs = obp;
    const obx = ob.x;
    if (obx < obs.x) {
      while (obs.xprev && obx < obs.xprev.x) {
        obs = obs.xprev;
      }
      ob.xprev = obs.xprev;
      ob.xnext = obs;
    } else {
      while (obs.xnext && obx > obs.xnext.x) {
        obs = obs.xnext;
      }
      ob.xprev = obs;
      ob.xnext = obs.xnext;
    }
    if (ob.xprev) {
      ob.xprev.xnext = ob;
    }
    if (ob.xnext) {
      ob.xnext.xprev = ob;
    }
  }

  /** Remove `ob` from the X-sorted list; returns a neighbour to re-insert from. */
  deletex(ob: Obj): Obj {
    let oldpos: Obj = this.topobj;
    if (ob.xprev) {
      oldpos = ob.xprev;
      ob.xprev.xnext = ob.xnext;
    }
    if (ob.xnext) {
      oldpos = ob.xnext;
      ob.xnext.xprev = ob.xprev;
    }
    ob.xprev = null;
    ob.xnext = null;
    return oldpos;
  }

  /** Re-sort `ob` after its X changed (no-op if it is not in the list). */
  updateobjpos(ob: Obj): void {
    if (!ob.xprev && !ob.xnext) {
      return;
    }
    this.insertx(ob, this.deletex(ob));
  }

  /** Move an object one tick and keep it sorted in the X list. */
  movexy(ob: Obj, maxX: number, out: { x: number; y: number }): void {
    movexy(ob, maxX, out);
    this.updateobjpos(ob);
  }

  /** Iterate the main list safely (the callback may deallocate `ob`). */
  forEach(fn: (ob: Obj) => void): void {
    let ob = this.objtop;
    while (ob) {
      const next = ob.next;
      fn(ob);
      ob = next;
    }
  }

  /** Snapshot of the main list, for renderers and tests. */
  toArray(): Obj[] {
    const out: Obj[] = [];
    this.forEach((ob) => out.push(ob));
    return out;
  }
}

/** Copy an object's fields (not its list links) into another. */
export function copyobj(to: Obj, from: Obj): void {
  Object.assign(to, from);
  to.xprev = null;
  to.xnext = null;
  to.prev = null;
  to.next = null;
}

function resetObj(ob: Obj): void {
  const fresh = new Obj();
  Object.assign(ob, fresh);
  ob.state = ObjState.WAITING;
}
